import { ScrollView } from "react-native";

import { useBoxStyle } from "arkh";
import React, { useCallback, useMemo } from "react";

import { KPIEditScreenProps } from "../KPIEditParamList";

import { parseFillMode } from "../../components/produceScheduleRule";
import { KPIPropertiesComponent } from "../../components/KPIPropertiesComponent";
import { remoteKPIToKPITeamGroups } from "../remoteKPIToKPITeamGroups";
import { notEmpty } from "../../../../utils";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { LazyQueryNetworkBoundrary } from "../../../../components/mobile/QueryNetworkBoundrary";
import { useModalInsets } from "../../../../components/mobile/useModalInsets";
import {
  KPIEditMainQuery,
  KPIEditMainQuery$data,
} from "./__generated__/KPIEditMainQuery.graphql";
import { useNavigationHeader } from "@/src/common/navigationHeaderOptions";
import { useIntlTokens } from "@/src/components/mobile/useIntlTokens";
import { useIntl } from "react-intl";
import { useAlert } from "@/src/common/Alert";

const KPI_EDIT_MAIN_QUERY = graphql`
  query KPIEditMainQuery($routineId: ID!) {
    routine(id: $routineId) {
      id
      title
      team {
        id
        icon
        name
        color
      }
      activeVersion {
        tasks {
          id
          __typename
          ... on TaskNumber {
            unit
            goals {
              greaterThan
              lessThan
              section {
                id
              }
            }
          }
        }
      }
      activeSections {
        id
        assignedTeam {
          id
          name
          icon
          color
        }
        title
      }
      schedule {
        rules {
          freq
          byhour
          byminute
          byweekday
          tasks {
            taskId
          }
        }
      }
    }
  }
`;

export function KPIEditMain(props: KPIEditScreenProps<"KPIEditMain">) {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fetchKey) => <InnerKPIEditMain {...props} fetchKey={fetchKey} />}
    </LazyQueryNetworkBoundrary>
  );
}

type Task = {
  readonly __typename: string;
  readonly goals?:
    | readonly {
        readonly greaterThan: number | null;
        readonly lessThan: number | null;
        readonly section: {
          readonly id: string;
        };
      }[]
    | undefined;
  readonly id: string;
  readonly unit?: "CURRENCY" | "CUSTOM" | undefined;
};

function InnerKPIEditMain(
  props: KPIEditScreenProps<"KPIEditMain"> & { fetchKey: string }
) {
  const data = useLazyLoadQuery<KPIEditMainQuery>(
    KPI_EDIT_MAIN_QUERY,
    {
      routineId: props.route.params.routineId,
    },
    { fetchKey: props.fetchKey, fetchPolicy: "store-and-network" }
  );

  const task = useMemo(
    () => data.routine?.activeVersion.tasks[0],
    [data.routine?.activeVersion.tasks]
  );

  if (
    task &&
    task.__typename === "TaskNumber" &&
    data.routine?.schedule?.rules[0]
  ) {
    return <C {...props} data={data} task={task} />;
  }

  return null;
}

function C(
  props: KPIEditScreenProps<"KPIEditMain"> & {
    data: KPIEditMainQuery$data;
    task: Task;
  }
) {
  const firstGoal = props.task.goals?.[0];
  const hasGoal =
    (firstGoal?.greaterThan ?? null) !== null ||
    (firstGoal?.lessThan ?? null) !== null;
  const goalDirection =
    (firstGoal?.lessThan ?? null) !== null ? "less" : "greater";

  const routine = props.data.routine;
  if (!routine) {
    throw new Error("KPIEditMain: Routine not found");
  }

  const rules = routine?.schedule?.rules;
  if (!rules) {
    throw new Error("KPIEditMain: Rules not found");
  }

  const teamGroups = remoteKPIToKPITeamGroups({
    activeSections: routine.activeSections,
    goals: props.task.goals ?? [],
  });

  const fillMode = useMemo(
    () => parseFillMode(rules[0].byhour, rules[0].byminute),
    [rules]
  );

  const selectedDays = rules.flatMap((r) => r.byweekday ?? []).filter(notEmpty);

  const onChangeFrequency = useCallback(() => {
    props.navigation.navigateDeprecated("KPIEditFrequency", {
      taskId: rules[0].tasks[0].taskId,
      fillMode,
      selectedDays:
        fillMode === "previous" &&
        selectedDays.length === 1 &&
        selectedDays[0] === 6
          ? [0]
          : selectedDays,
      routineId: props.route.params.routineId,
      title: props.route.params.title,
    });
  }, [
    props.navigation,
    props.route.params.routineId,
    props.route.params.title,
    rules,
    fillMode,
    selectedDays,
  ]);
  const baseParams = useMemo(
    () => ({
      routineId: props.route.params.routineId,
      title: props.route.params.title,
    }),
    [props.route.params.routineId, props.route.params.title]
  );
  const onChangeAdminTeam = useCallback(() => {
    props.navigation.navigateDeprecated("KPIEditAdminTeam", baseParams);
  }, [baseParams, props.navigation]);
  const onChangeTitle = useCallback(() => {
    props.navigation.navigateDeprecated("KPIEditTitle", {
      routineId: props.route.params.routineId,
      title: routine.title,
    });
  }, [props.navigation, props.route.params.routineId, routine.title]);
  const onChangeUnit = useCallback(() => {
    props.navigation.navigateDeprecated("KPIEditUnit", {
      routineId: props.route.params.routineId,
      title: props.route.params.title,
      unit: props.task.unit ?? "CUSTOM",
      taskId: props.task.id,
    });
  }, [
    props.navigation,
    props.route.params.routineId,
    props.route.params.title,
    props.task.id,
    props.task.unit,
  ]);
  const onChangeSections = useCallback(async () => {
    props.navigation.navigateDeprecated("KPIEditSections", {
      routineId: props.route.params.routineId,
      title: props.route.params.title,
      localSections: [],
      removeSections: [],
    });
  }, [
    props.navigation,
    props.route.params.routineId,
    props.route.params.title,
  ]);
  const insets = useModalInsets();
  const contentContainerStyle = useBoxStyle({
    space: "medium",
    insets,
  });

  const intl_tokens = useIntlTokens();

  const intl = useIntl();
  const [deleteKPI,] = useMutation(graphql`
    mutation KPIEditMainDeleteKPIButtonMutation($id: ID!) {
      routine(id: $id) {
        delete
      }
    }
  `);

  const onDeleteKPI = useCallback(() => {
    deleteKPI({
      variables: {
        id: props.route.params.routineId,
      },
      onCompleted: () => props.navigation.goBack(),
    });
  }, [deleteKPI, props.navigation, props.route.params.routineId]);

  const Alert = useAlert();

  function onDelete() {
    Alert.alert(
      intl.formatMessage({
        defaultMessage: "Do you want to delete this KPI?",
        description: "DeleteKPIButton alert title",
      }),
      intl.formatMessage({
        defaultMessage: "All history collected from this KPI will be deleted",
        description: "DeleteKPIButton alert description",
      }),
      [
        { style: "cancel", text: intl_tokens.Cancel },
        {
          style: "destructive",
          text: intl_tokens.Delete,
          onPress: onDeleteKPI,
        },
      ]
    );
  }

  useNavigationHeader({
    type: "CLOSE",
    onClose: () => props.navigation.goBack(),
    headerTitle: intl_tokens.EditKPI,
    defaultContentStyle: true,
    more: [
      {
        icon: "trash",
        onPress: onDelete,
        label: intl_tokens.Delete,

        color: "negative",
        type: "button",
      },
    ],
  });

  return (
    <ScrollView
      style={{
        flex: 1,
      }}
      contentContainerStyle={contentContainerStyle}
      contentInsetAdjustmentBehavior="always"
    >
      <KPIPropertiesComponent
        title={routine.title ?? props.route.params.title}
        fillMode={fillMode}
        goalDirection={goalDirection}
        hasGoal={hasGoal}
        byweekday={selectedDays}
        teamGroup={teamGroups}
        team={routine.team}
        unit={props.task.unit ?? "CUSTOM"}
        onChangeUnit={onChangeUnit}
        onChangeTitle={onChangeTitle}
        onChangeFrequency={onChangeFrequency}
        onChangeAdminTeam={onChangeAdminTeam}
        onChangeSections={onChangeSections}
      />
    </ScrollView>
  );
}
